import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { api, IMG, PURE_SOULS_URL } from "@/lib/site";
import SectionHeader from "@/components/site/SectionHeader";
import FeaturedProviderCard from "@/components/site/FeaturedProviderCard";
import AustraliaMap from "@/components/site/AustraliaMap";
import Reveal from "@/components/site/Reveal";
import Seo from "@/components/site/Seo";
import NearMeSearch from "@/components/site/NearMeSearch";
import GetListedDialog from "@/components/site/GetListedDialog";
import { sortByDistance, formatKm } from "@/lib/providerGeo";
import {
  PAGE_SEO,
  breadcrumbLd,
  PURE_SOULS_LD,
  providersItemListLd,
} from "@/lib/seo";

const stateNames = {
  QLD: "Queensland",
  NSW: "New South Wales",
  VIC: "Victoria",
  WA: "Western Australia",
  SA: "South Australia",
  TAS: "Tasmania",
  NT: "Northern Territory",
  ACT: "Australian Capital Territory",
};

export default function Providers() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLoc, setUserLoc] = useState(null);

  useEffect(() => {
    api
      .get("/providers")
      .then((res) => setProviders(res.data))
      .catch(() => setProviders([]))
      .finally(() => setLoading(false));
  }, []);

  const grouped = providers.reduce((acc, p) => {
    (acc[p.state] = acc[p.state] || []).push(p);
    return acc;
  }, {});

  const order = ["QLD", "NSW", "VIC", "WA", "SA", "TAS", "NT", "ACT"];

  const ranked = useMemo(
    () => (userLoc ? sortByDistance(providers, userLoc) : []),
    [userLoc, providers]
  );
  const nearestName = ranked.find((p) => p.distanceKm != null)?.name || null;

  return (
    <div data-testid="providers-page">
      <Seo
        {...PAGE_SEO.providers}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Providers", path: "/providers" },
          ]),
          PURE_SOULS_LD,
          providersItemListLd(providers),
        ]}
      />
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.heroFog} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#F9F9F6]/85" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F9F9F6]/40 via-transparent to-[#F9F9F6]" />
        </div>
        <div className="aura bg-sage-300/28 w-[40vw] h-[40vw] -top-[12vw] -left-[10vw]" />
        <div className="aura bg-water-300/36 w-[32vw] h-[32vw] top-[10%] -right-[8vw]" />
        <div className="container-editorial pt-20 pb-16">
          <div className="overline mb-5">Directory</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-forest-900 max-w-4xl">
            Water cremation providers in Australia
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-forest-700 leading-[1.65]">
            Water cremation services are gradually becoming more available across
            Australia as awareness around sustainable pet aftercare continues to
            grow. The directory begins here.
          </p>
        </div>
      </section>

      {/* Find a provider near you */}
      <section className="container-editorial pt-2 sm:pt-4" data-testid="near-me-section">
        <NearMeSearch
          onLocate={setUserLoc}
          onClear={() => setUserLoc(null)}
          active={userLoc}
        />

        {userLoc && (
          <div className="mt-8" data-testid="near-me-results">
            <div className="overline mb-4">Nearest providers</div>
            <div className="space-y-4">
              {ranked.map((p) => (
                <a
                  key={p.id}
                  href={p.website || PURE_SOULS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-4 glass rounded-2xl px-5 py-4 sm:px-6 sm:py-5 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-card min-h-[64px]"
                  data-testid={`near-me-result-${p.id}`}
                >
                  <div className="min-w-0">
                    <div className="overline mb-1">
                      {(p.geo && p.geo.city) || p.location} · {p.state}
                      {p.featured ? " · Featured" : ""}
                    </div>
                    <h3 className="font-serif text-xl text-forest-900 leading-tight truncate">
                      {p.name}
                    </h3>
                  </div>
                  <div className="text-right shrink-0">
                    {p.distanceKm != null && (
                      <div
                        className="font-serif text-xl sm:text-2xl text-forest-900 leading-none"
                        data-testid={`near-me-distance-${p.id}`}
                      >
                        {formatKm(p.distanceKm)}
                      </div>
                    )}
                    <span className="link-underline text-sm inline-flex items-center gap-1 mt-1">
                      Visit <ArrowUpRight size={14} />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </section>

      <section
        className="bg-sand-100 section-padding mt-12 sm:mt-16"
        data-testid="availability-map-section"
      >
        <div className="container-editorial">
          <SectionHeader
            overline="Provider Map"
            title="Verified providers across Australia"
            intro="Tap a paw marker on the map, or a name in the list, to see a provider's details and visit them directly. The map grows as new providers join the directory."
          />
          <AustraliaMap providers={providers} focusName={nearestName} />
        </div>
      </section>

      <section className="relative isolate overflow-hidden section-padding" data-testid="providers-list-section">
        <div className="aura bg-clay-200/20 w-[38vw] h-[38vw] top-[-6vw] right-[-8vw]" />
        <div className="aura bg-water-300/24 w-[40vw] h-[40vw] bottom-[-10vw] left-[-10vw]" />
        <div className="container-editorial relative">
        {loading && (
          <p className="text-forest-700" data-testid="providers-loading">
            Loading providers…
          </p>
        )}

        {!loading &&
          order.map((state) => {
            const list = grouped[state] || [];
            if (list.length === 0) return null;
            return (
              <Reveal key={state} className="mb-20 last:mb-0">
                <div data-testid={`providers-state-${state}`}>
                  <div className="flex items-end justify-between flex-wrap gap-4 mb-10 border-b border-sand-300 pb-6">
                    <div>
                      <div className="overline mb-2">State</div>
                      <h2 className="font-serif text-4xl text-forest-900 tracking-tight">
                        {stateNames[state]}
                      </h2>
                    </div>
                    <span className="text-sm text-forest-700">
                      {list.length} listing{list.length !== 1 ? "s" : ""}
                    </span>
                  </div>

                  {state === "QLD" && (
                    <div className="mb-10">
                      <FeaturedProviderCard variant="full" />
                    </div>
                  )}

                  <div className="space-y-6">
                    {list
                      .filter((p) => !p.featured)
                      .map((p) => (
                        <div
                          key={p.id}
                          className="grid grid-cols-1 md:grid-cols-12 gap-6 glass-strong card-hover rounded-2xl p-6 sm:p-8"
                          data-testid={`provider-card-${p.id}`}
                        >
                          <div className="md:col-span-3">
                            <div className="overline">{p.location}</div>
                            <h3 className="font-serif text-2xl text-forest-900 mt-2 leading-tight">
                              {p.name}
                            </h3>
                          </div>
                          <div className="md:col-span-7 text-forest-700 leading-relaxed">
                            {p.description}
                            {p.services && p.services.length > 0 && (
                              <div
                                className="flex flex-wrap gap-2 mt-4"
                                data-testid={`provider-services-${p.id}`}
                              >
                                {p.services.map((s) => (
                                  <span
                                    key={s}
                                    className="text-xs tracking-wide text-sage-700 bg-sage-50 border border-sage-100 rounded-full px-3 py-1"
                                  >
                                    {s}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="md:col-span-2 md:flex md:items-center md:justify-end">
                            <a
                              href={p.website || PURE_SOULS_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="link-underline text-sm"
                              data-testid={`provider-link-${p.id}`}
                            >
                              Learn more →
                            </a>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </Reveal>
            );
          })}

        {/* Get listed */}
        <div className="border-t border-sand-300 pt-10 mt-16 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-serif text-2xl text-forest-900 tracking-tight mb-2">
              Do you operate a water cremation service?
            </h2>
            <p className="text-sm text-forest-700 leading-relaxed">
              If you run a genuine pet water cremation service in Australia, request
              inclusion in this educational directory. We review every submission.
            </p>
          </div>
          <GetListedDialog
            triggerLabel="Get listed"
            triggerClassName="btn-primary shrink-0"
            triggerTestId="providers-get-listed"
          />
        </div>
        </div>
      </section>
    </div>
  );
}
